// Fuso horário dos agendamentos. Cada tarefa com horário guarda o fuso em que a hora foi marcada
// (ex.: audiência às 14:00 em Campo Grande). Na tela e nos avisos, a hora aparece convertida para o
// fuso do aparelho, onde quer que a pessoa esteja. Tarefas antigas, sem fuso, seguem a hora do aparelho.
import type { Task } from '../store/types';

const p2 = (n: number) => String(n).padStart(2, '0');

export function fusoAparelho(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

const cacheFmt = new Map<string, Intl.DateTimeFormat>();
function partes(tz: string, ms: number) {
  let f = cacheFmt.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' } as any);
    cacheFmt.set(tz, f);
  }
  const d = new Date(ms);
  if (typeof (f as any).formatToParts === 'function') {
    const o: Record<string, number> = {};
    for (const p of f.formatToParts(d)) if (p.type !== 'literal') o[p.type] = Number(p.value);
    return { y: o.year, m: o.month, d: o.day, h: o.hour % 24, mi: o.minute };
  }
  const x = f.format(d).match(/(\d+)\/(\d+)\/(\d+),?\s+(\d+):(\d+)/);   // "10/05/2026, 14:00:00"
  if (!x) throw new Error('fuso');
  return { y: Number(x[3]), m: Number(x[1]), d: Number(x[2]), h: Number(x[4]) % 24, mi: Number(x[5]) };
}

/** Diferença do fuso para o UTC, em minutos, num instante (ex.: São Paulo = -180). */
export function deslocamento(tz: string, ms = Date.now()) {
  try { const p = partes(tz, ms); return Math.round((Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi) - Math.floor(ms / 60000) * 60000) / 60000); }
  catch { return -new Date(ms).getTimezoneOffset(); }
}

/** Instante real de "data + hora" num fuso. */
export function instante(data: string, hora: string, tz?: string | null): Date {
  const [y, m, d] = data.split('-').map(Number);
  const [h, mi] = hora.split(':').map(Number);
  if (!tz || tz === fusoAparelho()) return new Date(y, m - 1, d, h, mi, 0, 0);
  const chute = Date.UTC(y, m - 1, d, h, mi);
  let r = chute - deslocamento(tz, chute) * 60000;
  const o2 = deslocamento(tz, r);
  r = chute - o2 * 60000;
  return new Date(r);
}

/** A tarefa no relógio do aparelho: data e hora convertidas (ou as mesmas, se o fuso for igual). */
export function noAparelho(tk: Pick<Task, 'time' | 'tz'>, dia: string): { dia: string; hora: string | null; outro: boolean } {
  if (!tk.time || !tk.tz || tk.tz === fusoAparelho()) return { dia, hora: tk.time, outro: false };
  const x = instante(dia, tk.time, tk.tz);
  return { dia: `${x.getFullYear()}-${p2(x.getMonth() + 1)}-${p2(x.getDate())}`, hora: `${p2(x.getHours())}:${p2(x.getMinutes())}`, outro: true };
}

/** "GMT-3", "GMT+5:30" */
export function rotuloGMT(tz: string) {
  const o = deslocamento(tz);
  const s = o < 0 ? '-' : '+', a = Math.abs(o);
  return `GMT${s}${Math.floor(a / 60)}${a % 60 ? ':' + p2(a % 60) : ''}`;
}
/** "Campo Grande (GMT-4)" */
// texto 19 (I): os nomes dos fusos vêm sem acento ("Sao Paulo"): os que têm acento ficam certos na tela
const ACENTOS: Record<string, string> = { 'Sao Paulo': 'São Paulo', 'Maceio': 'Maceió', 'Belem': 'Belém', 'Araguaina': 'Araguaína', 'Cuiaba': 'Cuiabá',
  'Santarem': 'Santarém', 'Eirunepe': 'Eirunepé', 'Asuncion': 'Asunción', 'Bogota': 'Bogotá', 'Reykjavik': 'Reykjavík', 'Ciudad Juarez': 'Ciudad Juárez',
  'Merida': 'Mérida', 'Mazatlan': 'Mazatlán', 'Cancun': 'Cancún', 'Bahia Banderas': 'Bahía de Banderas', 'Sao Tome': 'São Tomé', 'Curacao': 'Curaçao' };
const comAcento = (x: string) => ACENTOS[x] ?? x;
export const nomeFuso = (tz: string) => `${comAcento(tz.split('/').pop()!.replace(/_/g, ' '))} (${rotuloGMT(tz)})`;
/** "America/Campo_Grande" → "América · Campo Grande" para a lista. */
export const caminhoFuso = (tz: string) => tz.replace(/_/g, ' ').split('/').map(comAcento).join(' · ');

const BASICOS = [
  'America/Noronha', 'America/Sao_Paulo', 'America/Bahia', 'America/Fortaleza', 'America/Recife', 'America/Maceio', 'America/Belem', 'America/Araguaina',
  'America/Campo_Grande', 'America/Cuiaba', 'America/Manaus', 'America/Boa_Vista', 'America/Porto_Velho', 'America/Rio_Branco',
  'America/Argentina/Buenos_Aires', 'America/Montevideo', 'America/Asuncion', 'America/Santiago', 'America/La_Paz', 'America/Lima', 'America/Bogota',
  'America/Caracas', 'America/Panama', 'America/Mexico_City', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'America/Toronto', 'America/Vancouver', 'America/Halifax', 'America/St_Johns', 'Pacific/Honolulu',
  'Atlantic/Azores', 'Atlantic/Cape_Verde', 'Europe/Lisbon', 'Europe/London', 'Europe/Dublin', 'Europe/Madrid', 'Europe/Paris', 'Europe/Brussels',
  'Europe/Amsterdam', 'Europe/Berlin', 'Europe/Zurich', 'Europe/Rome', 'Europe/Vienna', 'Europe/Warsaw', 'Europe/Prague', 'Europe/Stockholm',
  'Europe/Athens', 'Europe/Helsinki', 'Europe/Istanbul', 'Europe/Kyiv', 'Europe/Moscow', 'Africa/Casablanca', 'Africa/Lagos', 'Africa/Luanda',
  'Africa/Maputo', 'Africa/Johannesburg', 'Africa/Cairo', 'Africa/Nairobi', 'Asia/Jerusalem', 'Asia/Riyadh', 'Asia/Dubai', 'Asia/Tehran',
  'Asia/Karachi', 'Asia/Kolkata', 'Asia/Kathmandu', 'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Singapore', 'Asia/Kuala_Lumpur',
  'Asia/Manila', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Taipei', 'Asia/Seoul', 'Asia/Tokyo', 'Australia/Perth', 'Australia/Adelaide',
  'Australia/Sydney', 'Australia/Brisbane', 'Pacific/Auckland', 'Pacific/Fiji', 'UTC',
];
/** Todos os fusos que o aparelho conhece (ou uma lista básica dos principais). */
export function listaFusos(): string[] {
  let l: string[] = [];
  try { l = (Intl as any).supportedValuesOf?.('timeZone') ?? []; } catch { /* sem lista */ }
  const todos = [...new Set([...(l.length ? l : BASICOS), fusoAparelho()])];
  return todos.filter(tz => { try { deslocamento(tz); return true; } catch { return false; } });
}

/** Hora agora num fuso ("14:05"). */
export function horaEm(tz: string, ms = Date.now()) {
  const d = new Date(ms + deslocamento(tz, ms) * 60000);
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
}
